import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import routes from '../../routers'
import { EMAIL_PATTERN } from '../../utils/constants'
import { inviteToGroup } from '../../api/group'
import { apiError } from '../../api/account'
import Layout from '../../components/Layout'

// Global classes still used by the sign-up invite screens
import './styles.css'
import styles from './InviteFamMember.module.css'

const MAX_PEOPLE = 10

const listNames = (emails) =>
  emails.length < 3
    ? emails.join(' and ')
    : `${emails.slice(0, -1).join(', ')} and ${emails[emails.length - 1]}`

// Invite people to a family group: by email, or by sharing the group's private link.
// Opens on a group when the link says which (/invite?group=12).
const InviteFamMember = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const groups = useSelector((state) => state.groups?.data) || []
  const [groupId, setGroupId] = useState(new URLSearchParams(search).get('group') || '')
  const [emails, setEmails] = useState([''])
  const [badRows, setBadRows] = useState([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState({ text: '', ok: true })
  const [copied, setCopied] = useState(false)
  const lastInput = useRef(null)
  const focusNew = useRef(false)

  const group = groups.find((g) => String(g.id) === String(groupId)) || groups[0]

  useEffect(() => {
    if (focusNew.current) lastInput.current?.focus()
    focusNew.current = false
  }, [emails.length])

  const setEmail = (index, value) => {
    setEmails((list) => list.map((e, i) => (i === index ? value : e)))
    setBadRows((rows) => rows.filter((i) => i !== index))
  }

  const addRow = () => {
    focusNew.current = true
    setEmails((list) => [...list, ''])
  }

  const removeRow = (index) => {
    setEmails((list) => list.filter((_, i) => i !== index))
    setBadRows([])
  }

  const send = (e) => {
    e.preventDefault()
    const filled = emails.map((v, i) => [v.trim(), i]).filter(([v]) => v)
    const bad = filled.filter(([v]) => !EMAIL_PATTERN.test(v)).map(([, i]) => i)
    if (!filled.length) return setMessage({ text: 'Add at least one email address.', ok: false })
    if (bad.length) {
      setBadRows(bad)
      return setMessage({ text: 'Check the highlighted email address.', ok: false })
    }
    const list = [...new Set(filled.map(([v]) => v.toLowerCase()))]
    setBusy(true)
    setMessage({ text: '', ok: true })
    inviteToGroup({ emails: list.map((email) => ({ email })), group_id: group.id })
      .then(() => {
        setEmails([''])
        setMessage({
          text: `Invite sent to ${listNames(list)}. Give them a heads-up so they look for it.`,
          ok: true,
        })
      })
      .catch((err) =>
        setMessage({
          text: apiError(err, 'We could not send that invite. Please try again.'),
          ok: false,
        })
      )
      .finally(() => setBusy(false))
  }

  const copyLink = () => {
    navigator.clipboard?.writeText(group.inviteLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const shareLink = () => {
    navigator
      .share({
        title: `Join ${group.name} on Morsel`,
        text: `Join ${group.name} on Morsel so we can share our family recipes.`,
        url: group.inviteLink,
      })
      .catch(() => {})
  }

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate(routes.settings))

  return (
    <Layout title="Invite a Member">
      <div className={styles.page}>
        <button type="button" className={styles.back} onClick={back}>
          <span aria-hidden="true">&larr;</span> Back
        </button>
        <h1 className={styles.title}>Invite family</h1>
        <p className={styles.lead}>
          Bring the whole table together. Everyone you invite can read, cook and add recipes in your
          group.
        </p>

        {!groups.length ? (
          <section className={styles.card}>
            <h2 className={styles.h2}>Start a group first</h2>
            <p className={styles.small}>
              Invites go to a group, like &ldquo;The Smith Family&rdquo;. Create one and you can
              invite people right away.
            </p>
            <Link to={`${routes.settings}#create-group`} className={styles.btnDark}>
              Create a group
            </Link>
          </section>
        ) : (
          <>
            <form className={styles.card} onSubmit={send} noValidate>
              {groups.length > 1 ? (
                <fieldset className={styles.fieldset}>
                  <legend className={styles.label}>Invite them to</legend>
                  <div className={styles.chips}>
                    {groups.map((g) => (
                      <label
                        key={g.id}
                        className={`${styles.chip} ${g.id === group.id ? styles.chipOn : ''}`}
                      >
                        <input
                          type="radio"
                          name="group"
                          value={g.id}
                          checked={g.id === group.id}
                          onChange={() => setGroupId(String(g.id))}
                        />
                        {g.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : (
                <p className={styles.joining}>
                  Inviting to <b>{group.name}</b>
                </p>
              )}

              <div className={styles.label} id="invite-emails">
                Their email address
              </div>
              <div className={styles.emails} role="group" aria-labelledby="invite-emails">
                {emails.map((value, index) => (
                  <div key={index} className={styles.emailRow}>
                    <input
                      ref={index === emails.length - 1 ? lastInput : null}
                      className={`${styles.input} ${badRows.includes(index) ? styles.bad : ''}`}
                      type="email"
                      inputMode="email"
                      autoComplete="off"
                      placeholder="name@example.com"
                      aria-label={`Email address ${index + 1}`}
                      aria-invalid={badRows.includes(index)}
                      value={value}
                      onChange={(e) => setEmail(index, e.target.value)}
                    />
                    {emails.length > 1 ? (
                      <button
                        type="button"
                        className={styles.remove}
                        aria-label={`Remove email address ${index + 1}`}
                        onClick={() => removeRow(index)}
                      >
                        &times;
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              {emails.length < MAX_PEOPLE ? (
                <button type="button" className={styles.add} onClick={addRow}>
                  + Add another person
                </button>
              ) : null}

              <div className={styles.actions}>
                <button className={styles.btnDark} disabled={busy}>
                  {busy ? 'Sending...' : emails.length > 1 ? 'Send invites' : 'Send invite'}
                </button>
                <span className={styles.small}>
                  They&rsquo;ll get an email with a link to join.
                </span>
              </div>
              {message.text ? (
                <p className={message.ok ? styles.saved : styles.error} role="status">
                  {message.text}
                </p>
              ) : null}
            </form>

            {group.inviteLink ? (
              <section className={styles.share}>
                <h2 className={styles.h2}>Or share a link</h2>
                <p className={styles.small}>
                  Text it, or drop it in the family group chat. Anyone with this link can join{' '}
                  {group.name}.
                </p>
                <div className={styles.linkRow}>
                  <input
                    className={`${styles.input} ${styles.linkInput}`}
                    readOnly
                    value={group.inviteLink}
                    aria-label={`Private invite link for ${group.name}`}
                    onFocus={(e) => e.target.select()}
                  />
                  <button type="button" className={styles.btn} onClick={copyLink}>
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                  {navigator.share ? (
                    <button type="button" className={styles.btn} onClick={shareLink}>
                      Share
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </Layout>
  )
}

export default InviteFamMember
